(() => {
  if (window.ProVariantPicker) return;

  const instances = new WeakMap();
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getShells = (root) => Array.from(root?.querySelectorAll('[data-size-scroll-shell]') || []);
  const getGroup = (shell) => shell?.querySelector('[data-size-scroll-group]') || null;
  const getWidth = (shell) => getGroup(shell)?.dataset.sizeWidth || '';
  const getCheckedInput = (shell) => shell?.querySelector('input[type="radio"]:checked') || null;
  const getSelectedShell = (root) => getShells(root).find((shell) => getCheckedInput(shell)) || null;

  const formatSizeValue = (value) => String(value || '').replace(/(\d)\s*[xX]\s*(\d)/g, '$1×$2');

  const formatSizeText = (root) => {
    if (!root?.matches('variant-selects.pro-variant-picker')) return;

    root.querySelectorAll('.product-form__input--pro-size').forEach((group) => {
      group.querySelectorAll('.product-form__variant-pill-text').forEach((node) => {
        const formatted = formatSizeValue(node.textContent.trim());
        if (node.textContent.trim() !== formatted) node.textContent = formatted;
      });

      const selectedValue = group.querySelector('[data-selected-value]');
      const checkedInput = group.querySelector('input[type="radio"]:checked');
      if (selectedValue) selectedValue.textContent = formatSizeValue(checkedInput?.value || selectedValue.textContent);
    });
  };

  const updateShell = (shell) => {
    const group = getGroup(shell);
    const leftButton = shell?.querySelector('[data-size-scroll-direction="-1"]');
    const rightButton = shell?.querySelector('[data-size-scroll-direction="1"]');
    if (!group || !leftButton || !rightButton) return;

    if (!mobileQuery.matches) {
      shell.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
      leftButton.disabled = true;
      rightButton.disabled = true;
      return;
    }

    const maxScroll = Math.max(0, group.scrollWidth - group.clientWidth);
    const tolerance = 3;
    const isScrollable = maxScroll > tolerance;
    const isAtStart = !isScrollable || group.scrollLeft <= tolerance;
    const isAtEnd = !isScrollable || group.scrollLeft >= maxScroll - tolerance;

    shell.classList.toggle('is-scrollable', isScrollable);
    shell.classList.toggle('is-at-start', isAtStart);
    shell.classList.toggle('is-at-end', isAtEnd);
    leftButton.disabled = !isScrollable || isAtStart;
    rightButton.disabled = !isScrollable || isAtEnd;
  };

  const refresh = (root) => {
    if (!root?.isConnected) return;
    formatSizeText(root);
    getShells(root).forEach(updateShell);
  };

  const init = (root) => {
    if (!root?.matches?.('variant-selects.pro-variant-picker') || instances.has(root)) return root;

    const controller = new AbortController();
    const { signal } = controller;
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => refresh(root)) : null;

    getShells(root).forEach((shell) => {
      const group = getGroup(shell);
      if (!group) return;
      group.addEventListener('scroll', () => updateShell(shell), { passive: true, signal });
      resizeObserver?.observe(group);
    });

    root.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-size-scroll-direction]');
        if (!button || !root.contains(button)) return;

        const shell = button.closest('[data-size-scroll-shell]');
        const group = getGroup(shell);
        if (!group) return;

        event.preventDefault();
        const direction = Number(button.dataset.sizeScrollDirection || 0);
        const distance = Math.max(160, group.clientWidth * 0.72);
        group.scrollBy({
          left: direction * distance,
          behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
        });
      },
      { signal },
    );

    root.addEventListener('change', () => requestAnimationFrame(() => formatSizeText(root)), { signal });

    instances.set(root, { controller, resizeObserver });
    refresh(root);
    return root;
  };

  const destroy = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.controller.abort();
    instance.resizeObserver?.disconnect();
    instances.delete(root);
  };

  const capture = (root, target = null) => {
    if (!root?.matches?.('variant-selects.pro-variant-picker')) return null;

    const targetInput = target?.matches?.('.product-form__input--pro-size input[type="radio"]')
      ? target
      : null;
    const shell = targetInput?.closest('[data-size-scroll-shell]') || getSelectedShell(root);
    const group = getGroup(shell);
    const selectedInput = targetInput || getCheckedInput(shell);
    if (!shell || !group || !selectedInput) return null;

    return {
      productHandle: root.dataset.productHandle || '',
      width: getWidth(shell),
      value: selectedInput.value || '',
      scrollLeft: group.scrollLeft,
    };
  };

  const restore = (root, state = null) => {
    if (!root?.matches?.('variant-selects.pro-variant-picker')) return;
    init(root);

    const apply = () => {
      if (!root.isConnected) return;
      const selectedShell = getSelectedShell(root);
      const selectedInput = getCheckedInput(selectedShell);
      const canRestore =
        state &&
        (!state.productHandle || state.productHandle === (root.dataset.productHandle || '')) &&
        selectedShell &&
        state.width === getWidth(selectedShell) &&
        state.value === (selectedInput?.value || '');

      getShells(root).forEach((shell) => {
        const group = getGroup(shell);
        if (!group) return;
        const maxScroll = Math.max(0, group.scrollWidth - group.clientWidth);
        group.scrollLeft = shell === selectedShell && canRestore
          ? Math.min(Math.max(Number(state.scrollLeft) || 0, 0), maxScroll)
          : 0;
        updateShell(shell);
      });
      formatSizeText(root);
    };

    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('variant-selects.pro-variant-picker')) init(scope);
    scope.querySelectorAll?.('variant-selects.pro-variant-picker').forEach(init);
  };

  window.ProVariantPicker = { init, destroy, capture, restore, refresh, initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('product-info:loaded', (event) => initAll(event.target));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  mobileQuery.addEventListener?.('change', () => {
    document.querySelectorAll('variant-selects.pro-variant-picker').forEach(refresh);
  });
})();
